/**
 * The record over the years, day by day (6.3, benchmark: Hunt UK) — beyond
 * the window of any one rule: a calendar year per row of months, each day in
 * the state the record holds for it.
 *
 *   here       within the record, on no trip and confirmed;
 *   away       a day between a trip's departure and its return;
 *   travel     the day of a departure or of a return — counted as the
 *              country's rules say (Portugal counts both as away), so shown
 *              as its own;
 *   uncertain  a day nobody confirmed (the engine's runs, a trip's apart);
 *   planned    a day of a trip planned ahead;
 *   none       before the record starts, or after today with no trip planned.
 *
 * A trip still open runs to today. The years run from the record's start to
 * today's — or to the last planned trip's — the newest first. Pure: `today`
 * is the caller's.
 */
import { dateOf, dayOf, type IsoDate } from './dates';

export type InOutPresenceDay = 'here' | 'away' | 'travel' | 'uncertain' | 'planned' | 'none';

export interface InOutPresenceYear {
  year: number;
  /** Twelve months, each its days in order (28–31). */
  months: InOutPresenceDay[][];
  /** The days of the year in each state the record counts. */
  counts: { here: number; away: number; travel: number; uncertain: number; planned: number };
}

const MS_PER_DAY = 86_400_000;
const yearOf = (day: number) => new Date(day * MS_PER_DAY).getUTCFullYear();

export function inoutPresenceCalendar(input: {
  /** The first day the record covers — the earliest title's issue date; null without one. */
  recordStartsOn: IsoDate | null;
  today: IsoDate;
  absences: ReadonlyArray<{ departedOn: IsoDate; returnedOn: IsoDate | null }>;
  plannedTrips: ReadonlyArray<{ departsOn: IsoDate; returnsOn: IsoDate; state: string }>;
  uncertainPeriods: ReadonlyArray<{ from: IsoDate; to: IsoDate; away: boolean }>;
}): InOutPresenceYear[] {
  if (input.recordStartsOn === null) return [];
  const start = dayOf(input.recordStartsOn);
  const today = dayOf(input.today);
  if (start > today) return [];

  const state = new Map<number, InOutPresenceDay>();
  const mark = (from: number, to: number, as: InOutPresenceDay) => {
    for (let day = from; day <= to; day++) state.set(day, as);
  };
  for (const period of input.uncertainPeriods) {
    if (!period.away) mark(dayOf(period.from), dayOf(period.to), 'uncertain');
  }
  for (const absence of input.absences) {
    const out = dayOf(absence.departedOn);
    const back = absence.returnedOn === null ? today : dayOf(absence.returnedOn);
    mark(out, back, 'away');
    state.set(out, 'travel');
    if (absence.returnedOn !== null) state.set(back, 'travel');
  }
  const planned = input.plannedTrips.filter((trip) => trip.state === 'planned');
  let lastPlanned = today;
  for (const trip of planned) {
    const from = Math.max(dayOf(trip.departsOn), today + 1);
    const to = dayOf(trip.returnsOn);
    if (to < from) continue;
    mark(from, to, 'planned');
    lastPlanned = Math.max(lastPlanned, to);
  }

  const years: InOutPresenceYear[] = [];
  for (let year = yearOf(start); year <= yearOf(lastPlanned); year++) {
    const counts = { here: 0, away: 0, travel: 0, uncertain: 0, planned: 0 };
    const months: InOutPresenceDay[][] = [];
    for (let month = 0; month < 12; month++) {
      const first = Date.UTC(year, month, 1) / MS_PER_DAY;
      const last = Date.UTC(year, month + 1, 0) / MS_PER_DAY;
      const days: InOutPresenceDay[] = [];
      for (let day = first; day <= last; day++) {
        const held = state.get(day);
        const of: InOutPresenceDay =
          day < start
            ? 'none'
            : held === 'planned'
              ? 'planned'
              : day > today
                ? 'none'
                : (held ?? 'here');
        if (of !== 'none') counts[of] += 1;
        days.push(of);
      }
      months.push(days);
    }
    years.push({ year, months, counts });
  }
  return years.reverse();
}

/**
 * The months of a year a calendar draws (owner, 19 Oct 2026): from the first
 * the record reaches to the last — none before the record starts, none ahead
 * with nothing planned; a month between two it reaches stays, so the year
 * reads without a gap. The newest first, as the years are (owner, 20 Oct
 * 2026) — December above January; the days of a month still run from the
 * 1st. One rule for Citizen Web and the phone.
 */
export function inoutPresenceDrawnMonths(
  months: ReadonlyArray<ReadonlyArray<InOutPresenceDay>>
): Array<{ month: number; days: ReadonlyArray<InOutPresenceDay> }> {
  const reached = months.map((days) => days.some((day) => day !== 'none'));
  const first = reached.indexOf(true);
  const last = reached.lastIndexOf(true);
  if (first === -1) return [];
  return months
    .map((days, month) => ({ month, days }))
    .filter(({ month }) => month >= first && month <= last)
    .reverse();
}

/** The date of a day in the calendar — year, month (0–11), day of the month (1–31). */
export function inoutPresenceDate(year: number, month: number, dayOfMonth: number): IsoDate {
  return dateOf(Date.UTC(year, month, dayOfMonth) / MS_PER_DAY);
}
